import { toast } from 'sonner'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { handleError } from '@/services/errorHandler.js'
import { useParams } from 'react-router-dom'
import { GOOGLE_CLIENT_ID, BASE_URL } from '@/config-global.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useSyncEntitlements } from '@/hooks/useSyncEntitlements'
import { useRelevantIntegration } from '@/hooks/useRelevantIntegration.jsx'
import { IntegrationProviders } from '@/consts/index.js'
import * as API from '@/services/api.js'

const GOOGLE_ADS_SCOPE = 'https://www.googleapis.com/auth/adwords'

export const useGoogleAdsConnection = () => {
    const { accountId } = useParams()
    const { updateSpaceState } = useAuthContext()
    const { space } = useCurrentSpace()
    const syncEntitlements = useSyncEntitlements()
    const { integration: googleIntegration } = useRelevantIntegration(IntegrationProviders.GOOGLE_ADS)

    const connectToGoogleAds = async () => {
        try {
            toggleLoader(true)

            const state = accountId
            // Redirect URI points to the backend — Google redirects there with the auth code,
            // the backend exchanges tokens and creates the integration, then redirects to the client callback page
            const redirectUri = encodeURIComponent(`${BASE_URL}/integrations/google/connect`)

            const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=${GOOGLE_ADS_SCOPE}&access_type=offline&prompt=consent&state=${state}`

            window.location.replace(googleAuthUrl)
        } catch (e) {
            console.error(e)
            toast.error(e.message)
            handleError(e)
            toggleLoader(false)
        }
    }

    const handleGoogleAdAccountSelection = async (selectedAccountIds) => {
        try {
            toggleLoader(true)

            if (googleIntegration?.adAccounts) {
                const selectedAdAccounts = googleIntegration.adAccounts
                    .filter(account => selectedAccountIds.includes(account.id))
                    .map(account => ({
                        ...account,
                        provider: googleIntegration?.provider || 'GOOGLE_ADS'
                    }))

                const normalizeProvider = (provider) => String(provider || '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '')

                const existingNonGoogleAccounts = space?.adAccounts?.filter(account => {
                    const normalized = normalizeProvider(account.provider)
                    return normalized !== 'google' && normalized !== 'google_ads'
                }) || []
                const nextAdAccounts = [...existingNonGoogleAccounts, ...selectedAdAccounts]
                await API.updateSpace(space._id, { adAccounts: nextAdAccounts })

                const updatedSpace = { ...space, adAccounts: nextAdAccounts }
                updateSpaceState(space._id, updatedSpace)

                syncEntitlements()
                toast.success('Ad Accounts updated successfully')
            }
        } catch (e) {
            console.error(e)
            toast.error('An error occurred while updating ad accounts')
        } finally {
            toggleLoader(false)
        }
    }

    return { connectToGoogleAds, handleGoogleAdAccountSelection, googleIntegration }
}
